import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  FAMOUS_SQL_QUERY_MAX_TIMEOUT_SECONDS,
  FAMOUS_SQL_QUERY_MIN_TIMEOUT_SECONDS,
} from "@erp/workflows";

const TranslationRefreshReportSchema = z.object({
  customerName: z.string(),
  customerOriginId: z.string(),
  famousRowCount: z.number(),
  created: z.number(),
  updated: z.number(),
});

const refreshTranslationList = oc
  .input(
    z.object({
      customerId: z.string().uuid().optional(),
      stagedPurchaseOrderId: z.string().uuid().optional(),
    }),
  )
  .output(z.object({ reports: z.array(TranslationRefreshReportSchema) }));

const FamousSqlPreviewColumnSchema = z.object({
  /** Column name exactly as returned by Oracle (e.g. `ORDER_ID`). */
  name: z.string(),
  /** Sanitized output socket id derived from the column name. */
  socketId: z.string(),
  /** Mapped socket value type. */
  type: z.enum(["string", "number", "boolean", "json"]),
  /** Oracle type name for display (e.g. `VARCHAR2`). */
  dbTypeName: z.string().optional(),
});

const FamousSqlQueryPreviewInputSchema = z.object({
  sql: z.string().min(1).max(50_000),
  parameters: z
    .array(
      z.object({
        name: z.string(),
        type: z.enum(["string", "number", "date"]),
        value: z.string().optional(),
        required: z.boolean().optional(),
      }),
    )
    .prefault([]),
  /** Oracle round-trip timeout in seconds; defaults to the step default (120). */
  queryTimeoutSeconds: z
    .number()
    .int()
    .min(FAMOUS_SQL_QUERY_MIN_TIMEOUT_SECONDS)
    .max(FAMOUS_SQL_QUERY_MAX_TIMEOUT_SECONDS)
    .optional(),
});

const FamousSqlQueryPreviewOutputSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  columns: z.array(FamousSqlPreviewColumnSchema).optional(),
  rows: z.array(z.record(z.string(), z.unknown())).optional(),
  rowCount: z.number().optional(),
  truncated: z.boolean().optional(),
  durationMs: z.number().optional(),
});

/**
 * Runs a read-only SQL statement against the Famous Oracle database with a
 * small row cap, returning sample rows plus detected column metadata. Used by
 * the `famous_sql_query` workflow step's config panel to preview results and
 * derive typed output sockets.
 */
const previewSqlQuery = oc
  .input(FamousSqlQueryPreviewInputSchema)
  .output(FamousSqlQueryPreviewOutputSchema);

const FamousConnectionStatusSchema = z.object({
  /** Org-level gate: Famous module enabled or Famous integration connected. */
  integrated: z.boolean(),
  /** Server-level: Oracle credentials (ORA_USER / ORA_PASSWORD) are set. */
  configured: z.boolean(),
  /** Round-trip probe (SELECT 1 FROM DUAL) succeeded. */
  healthy: z.boolean(),
  probeLatencyMs: z.number().nullable(),
  /** Failure detail when not configured or the probe failed. */
  error: z.string().nullable(),
  checkedAt: z.string(),
});

const checkConnection = oc
  .input(z.void())
  .output(FamousConnectionStatusSchema)
  .route({
    method: "POST",
    summary: "Admin-only Famous Oracle connectivity probe",
  });

export const famousContracts = {
  refreshTranslationList,
  previewSqlQuery,
  checkConnection,
} as const;

export type FamousContracts = typeof famousContracts;

export type TranslationRefreshReport = z.infer<
  typeof TranslationRefreshReportSchema
>;

export type FamousSqlPreviewColumn = z.infer<
  typeof FamousSqlPreviewColumnSchema
>;

export type FamousSqlQueryPreviewInput = z.infer<
  typeof FamousSqlQueryPreviewInputSchema
>;

export type FamousSqlQueryPreviewOutput = z.infer<
  typeof FamousSqlQueryPreviewOutputSchema
>;

export type FamousConnectionStatus = z.infer<
  typeof FamousConnectionStatusSchema
>;
